/**
 * Cache Freshness Detection Service
 * 
 * Provides intelligent cache management by detecting cache age and freshness
 * to optimize SSE event processing and avoid unnecessary invalidations.
 */

import { QueryClient } from '@tanstack/react-query';

interface CacheFreshnessMetrics {
  queryKey: string;
  age: number; // milliseconds since last update
  isFresh: boolean;
  isStale: boolean;
  isEmpty: boolean;
  lastUpdated: number | null;
  dataSize?: number;
}

type CacheFreshnessState = 'fresh' | 'stale' | 'empty';

/**
 * Cache Freshness Detector
 * 
 * Analyzes React Query cache state to determine freshness and guide
 * intelligent event processing decisions.
 */
export class CacheFreshnessDetector {
  private readonly queryClient: QueryClient;
  private readonly FRESH_WINDOW = 2 * 60 * 1000; // 2 minutes
  private readonly STALE_WINDOW = 10 * 60 * 1000; // 10 minutes

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  /**
   * Check if a specific cache entry is fresh
   */
  public isCacheFresh(queryKey: string[]): boolean {
    const metrics = this.getCacheMetrics(queryKey);
    return metrics.isFresh;
  }

  /**
   * Get age of cache entry in milliseconds
   */
  public getCacheAge(queryKey: string[]): number {
    const metrics = this.getCacheMetrics(queryKey);
    return metrics.age;
  }

  /**
   * Get comprehensive cache metrics for a query
   */
  public getCacheMetrics(queryKey: string[]): CacheFreshnessMetrics {
    const queryState = this.queryClient.getQueryState(queryKey);
    const queryData = this.queryClient.getQueryData(queryKey);
    
    const now = Date.now();
    const lastUpdated = queryState?.dataUpdatedAt || null;
    const age = lastUpdated ? now - lastUpdated : Infinity;
    
    const isEmpty = !queryData || 
      (Array.isArray(queryData) && queryData.length === 0) ||
      (typeof queryData === 'object' && Object.keys(queryData).length === 0);
    
    const isFresh = !isEmpty && age <= this.FRESH_WINDOW;
    const isStale = !isEmpty && age > this.STALE_WINDOW;

    return {
      queryKey: queryKey.join(':'),
      age,
      isFresh,
      isStale,
      isEmpty,
      lastUpdated,
      dataSize: this.estimateDataSize(queryData)
    };
  }

  /**
   * Determine overall cache freshness across multiple critical queries
   */
  public getOverallCacheFreshness(effectiveTimezone: string = 'UTC'): CacheFreshnessState {
    const criticalQueries = [
      ['accountSummary'],
      ['strategySummary'],
      ['pnlTimeframes', effectiveTimezone],
      ['accountBalances'],
      ['closedTrades']
    ];

    let freshCount = 0;
    let emptyCount = 0;
    let totalQueries = criticalQueries.length;

    for (const queryKey of criticalQueries) {
      const metrics = this.getCacheMetrics(queryKey);
      
      if (metrics.isEmpty) {
        emptyCount++;
      } else if (metrics.isFresh) {
        freshCount++;
      }
    }

    // If more than half are empty, consider overall state empty
    if (emptyCount > totalQueries / 2) {
      return 'empty';
    }

    // If more than half are fresh, consider overall state fresh
    if (freshCount > totalQueries / 2) {
      return 'fresh';
    }

    // Otherwise, consider stale
    return 'stale';
  }

  /**
   * Check if we should skip event replay based on cache freshness
   */
  public shouldSkipEventReplay(effectiveTimezone: string = 'UTC'): boolean {
    const overallFreshness = this.getOverallCacheFreshness(effectiveTimezone);
    
    // Skip replay if cache is fresh (data is recent enough)
    return overallFreshness === 'fresh';
  }

  /**
   * Check if incremental updates cache is fresh
   * (specific to useIncrementalUpdates handler)
   */
  public isIncrementalUpdatesFresh(effectiveTimezone: string = 'UTC'): boolean {
    const incrementalQueries = [
      ['accountSummary'],
      ['strategySummary'], 
      ['accountBalances'],
      ['pnlTimeframes', effectiveTimezone]
    ];

    return incrementalQueries.every(queryKey => 
      this.isCacheFresh(queryKey)
    );
  }

  /**
   * Check if global SSE data is fresh
   * (specific to useGlobalSSE handler)
   */
  public isGlobalSSEDataFresh(effectiveTimezone: string = 'UTC'): boolean {
    const globalQueries = [
      ['closedTrades'],
      ['pnlTimeframes', effectiveTimezone],
      ['accountDaily', effectiveTimezone],
      ['strategyDaily'],
      ['monthlyCalendar', effectiveTimezone]
    ];

    return globalQueries.some(queryKey => 
      this.isCacheFresh(queryKey)
    );
  }

  /**
   * Determine if metrics_calculated events should be allowed
   * based on cache state and handler coordination
   */
  public shouldAllowMetricsEvent(effectiveTimezone: string = 'UTC'): boolean {
    const incrementalFresh = this.isIncrementalUpdatesFresh(effectiveTimezone);
    const overallFreshness = this.getOverallCacheFreshness(effectiveTimezone);
    
    // Allow if:
    // 1. Cache is empty (need initial data)
    // 2. Cache is stale (need refresh)
    // 3. Incremental updates are not fresh (need update)
    return overallFreshness === 'empty' || 
           overallFreshness === 'stale' || 
           !incrementalFresh;
  }

  /**
   * Get detailed freshness report for debugging
   */
  public getFreshnessReport(effectiveTimezone: string = 'UTC'): {
    overall: CacheFreshnessState;
    queries: CacheFreshnessMetrics[];
    recommendations: string[];
    summary: {
      total: number;
      fresh: number;
      stale: number;
      empty: number;
    };
  } {
    const criticalQueries = [
      ['accountSummary'],
      ['strategySummary'],
      ['pnlTimeframes', effectiveTimezone],
      ['accountBalances'],
      ['accountDaily', effectiveTimezone],
      ['strategyDaily'],
      ['monthlyCalendar', effectiveTimezone],
      ['closedTrades']
    ];

    const metrics = criticalQueries.map(queryKey => 
      this.getCacheMetrics(queryKey)
    );

    const summary = {
      total: metrics.length,
      fresh: metrics.filter(m => m.isFresh).length,
      stale: metrics.filter(m => m.isStale).length,
      empty: metrics.filter(m => m.isEmpty).length
    };

    const overall = this.getOverallCacheFreshness(effectiveTimezone);
    
    const recommendations: string[] = [];
    
    if (overall === 'empty') {
      recommendations.push('Cache is mostly empty - allow all events for initial data load');
    } else if (overall === 'fresh') {
      recommendations.push('Cache is fresh - consider skipping event replay on reconnection');
      recommendations.push('Metrics events should still be processed for real-time updates');
    } else {
      recommendations.push('Cache is stale - allow all events to refresh data');
      recommendations.push('Consider selective invalidation based on event types');
    }

    if (summary.fresh > 0 && summary.stale > 0) {
      recommendations.push('Mixed cache state - use selective processing based on query freshness');
    }

    return {
      overall,
      queries: metrics,
      recommendations,
      summary
    };
  }

  /**
   * Estimate data size for cache metrics
   */
  private estimateDataSize(data: any): number {
    if (!data) return 0;
    
    try {
      // Simple estimation based on JSON string length
      return JSON.stringify(data).length;
    } catch {
      // Fallback for non-serializable data
      if (Array.isArray(data)) return data.length;
      if (typeof data === 'object') return Object.keys(data).length;
      return 1;
    }
  }

  /**
   * Clear expired cache entries based on staleness
   */
  public clearStaleCache(effectiveTimezone: string = 'UTC'): number {
    const criticalQueries = [
      ['accountSummary'],
      ['strategySummary'],
      ['pnlTimeframes', effectiveTimezone],
      ['accountBalances'],
      ['accountDaily', effectiveTimezone],
      ['strategyDaily'],
      ['monthlyCalendar', effectiveTimezone],
      ['closedTrades']
    ];

    let clearedCount = 0;

    for (const queryKey of criticalQueries) {
      const metrics = this.getCacheMetrics(queryKey);
      
      if (metrics.isStale) {
        this.queryClient.removeQueries({ queryKey });
        clearedCount++;
      }
    }

    return clearedCount;
  }

  /**
   * Force refresh of stale cache entries
   */
  public async refreshStaleCache(effectiveTimezone: string = 'UTC'): Promise<{
    attempted: number;
    successful: number;
    failed: string[];
  }> {
    const criticalQueries = [
      ['accountSummary'],
      ['strategySummary'],
      ['pnlTimeframes', effectiveTimezone],
      ['accountBalances']
    ];

    const results = {
      attempted: 0,
      successful: 0,
      failed: [] as string[]
    };

    for (const queryKey of criticalQueries) {
      const metrics = this.getCacheMetrics(queryKey);
      
      if (metrics.isStale) {
        results.attempted++;
        
        try {
          await this.queryClient.refetchQueries({ 
            queryKey,
            type: 'active'
          });
          results.successful++;
        } catch (error) {
          results.failed.push(queryKey.join(':'));
        }
      }
    }

    return results;
  }
}

/**
 * Create cache freshness detector instance
 */
export const createCacheFreshnessDetector = (queryClient: QueryClient): CacheFreshnessDetector => {
  return new CacheFreshnessDetector(queryClient);
};